import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiWrite, list } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

// Field-level encryption console (part 152). The page never receives a plaintext account: the
// API returns the last-four hint, and rotation only moves ciphertexts between key epochs. Every
// write asks for confirmation because both actions change how an entire column is stored.

interface EncryptedColumn {
  table_name: string
  column_name: string
  key_version: number
  key_id?: string
  encrypted_rows: number
  pending_rows: number
  sample_masked?: string
  rotated_at?: string
  created_at?: string
}

interface EncryptResult {
  table_name: string
  column_name: string
  encrypted: number
  skipped: number
  key_version: number
  sample_masked?: string
}

interface RotationRow {
  table_name: string
  column_name: string
  rows: number
  from_version: number
  to_version: number
}

interface RotateResult {
  columns: RotationRow[]
  total_rows: number
}

const COLUMNS_KEY = ['field-crypto-columns']

type Confirmation =
  | { kind: 'encrypt'; column: EncryptedColumn }
  | { kind: 'rotate' }
  | null

export function FieldCrypto() {
  const qc = useQueryClient()
  const { me } = useAuth()
  const canWrite = me?.role === 'owner' || me?.role === 'admin'

  const [confirmation, setConfirmation] = useState<Confirmation>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const columns = useQuery({
    queryKey: COLUMNS_KEY,
    queryFn: () => list<EncryptedColumn>('/v1/field-crypto/columns'),
    staleTime: 10_000,
  })

  const encrypt = useMutation({
    mutationFn: (column: EncryptedColumn) =>
      apiWrite<EncryptResult>('/v1/field-crypto/encrypt', 'POST', {
        table: column.table_name,
        column: column.column_name,
      }),
    onSuccess: (result) => {
      setNotice(
        `เข้ารหัส ${result.table_name}.${result.column_name} แล้ว ${number(result.encrypted)} แถว` +
          (result.skipped > 0 ? ` · ข้ามแถวที่เข้ารหัสแล้ว ${number(result.skipped)} แถว` : ''),
      )
      setConfirmation(null)
      setError(null)
      void qc.invalidateQueries({ queryKey: COLUMNS_KEY })
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : 'เข้ารหัสคอลัมน์ไม่สำเร็จ')
      setConfirmation(null)
    },
  })

  const rotate = useMutation({
    mutationFn: () => apiWrite<RotateResult>('/v1/field-crypto/rotate', 'POST'),
    onSuccess: (result) => {
      setNotice(
        `หมุนกุญแจแล้ว ${number(result.total_rows)} แถว` +
          (result.columns.length > 0
            ? ` · ${result.columns.map((c) => `${c.table_name} เป็นเวอร์ชัน ${c.to_version}`).join(', ')}`
            : ''),
      )
      setConfirmation(null)
      setError(null)
      void qc.invalidateQueries({ queryKey: COLUMNS_KEY })
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : 'หมุนกุญแจไม่สำเร็จ')
      setConfirmation(null)
    },
  })

  const rows = columns.data ?? []
  const encryptedColumns = rows.filter((row) => row.key_version > 0).length
  const pendingRows = rows.reduce((sum, row) => sum + row.pending_rows, 0)
  const encryptedRows = rows.reduce((sum, row) => sum + row.encrypted_rows, 0)
  const newestVersion = rows.reduce((max, row) => (row.key_version > max ? row.key_version : max), 0)
  const busy = encrypt.isPending || rotate.isPending

  function askEncrypt(column: EncryptedColumn) {
    if (busy) return
    setNotice(null)
    setError(null)
    setConfirmation({ kind: 'encrypt', column })
  }

  function askRotate() {
    if (busy) return
    setNotice(null)
    setError(null)
    setConfirmation({ kind: 'rotate' })
  }

  return (
    <>
      <PageHead
        title="การเข้ารหัสข้อมูลรายฟิลด์"
        sub="เข้ารหัสเลขบัญชีธนาคารด้วย AES-256-GCM · ค่าต้นฉบับถูกล้างออกจากคอลัมน์ · หมุนกุญแจได้โดยไม่ต้องหยุดใช้งาน"
        actions={
          canWrite ? (
            <button className="btn btn-primary" disabled={busy || encryptedRows === 0} onClick={askRotate}>
              หมุนกุญแจทั้งหมด
            </button>
          ) : undefined
        }
      />

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className="chip">{number(encryptedRows)} แถวที่เข้ารหัสแล้ว</span>
        <span className="chip">{number(pendingRows)} แถวรอเข้ารหัส</span>
        <span className="chip">{encryptedColumns} จาก {rows.length} คอลัมน์</span>
        <span className="chip">กุญแจเวอร์ชันล่าสุด {newestVersion === 0 ? 'ยังไม่มี' : newestVersion}</span>
      </div>

      {confirmation && (
        <div className="card card-pad" style={{ marginBottom: 16, borderColor: 'var(--color-amber)' }}>
          <div className="card-label">
            {confirmation.kind === 'rotate' ? 'ยืนยันการหมุนกุญแจ' : 'ยืนยันการเข้ารหัสคอลัมน์'}
          </div>
          {confirmation.kind === 'rotate' ? (
            <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.8, marginTop: 8 }}>
              ระบบจะถอดรหัสและเข้ารหัสใหม่ทุกคอลัมน์ที่ลงทะเบียนไว้ด้วยเวอร์ชันกุญแจถัดไป
              ภายในธุรกรรมเดียว · หากแถวใดล้มเหลว ทั้งชุดจะย้อนกลับและยังอยู่ที่เวอร์ชันเดิม
            </p>
          ) : (
            <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.8, marginTop: 8 }}>
              ระบบจะอ่านค่าจาก <span className="mono">{confirmation.column.table_name}.{confirmation.column.column_name}</span>{' '}
              เข้ารหัส แล้วล้างค่าต้นฉบับในคอลัมน์นั้น · แถวที่เข้ารหัสแล้วจะถูกข้าม
            </p>
          )}
          <div className="row" style={{ gap: 10, marginTop: 14 }}>
            <button
              className="btn btn-primary"
              disabled={busy}
              onClick={() => {
                if (!confirmation) return
                if (confirmation.kind === 'rotate') rotate.mutate()
                else encrypt.mutate(confirmation.column)
              }}>
              {busy ? 'กำลังดำเนินการ…' : confirmation.kind === 'rotate' ? 'ยืนยันหมุนกุญแจ' : 'ยืนยันเข้ารหัส'}
            </button>
            <button className="btn btn-ghost" disabled={busy} onClick={() => setConfirmation(null)}>
              ยกเลิก
            </button>
            <span className="dim" style={{ fontSize: 11.5 }}>
              การดำเนินการจะถูกบันทึกในร่องรอยการตรวจสอบ
            </span>
          </div>
        </div>
      )}

      {notice ? <div className="chip" style={{ marginBottom: 14 }}>{notice}</div> : null}
      {error ? <div className="form-error" style={{ marginBottom: 14 }}>{error}</div> : null}

      {columns.isLoading ? (
        <div className="stack"><Skel h={58} /><Skel h={58} /></div>
      ) : columns.isError ? (
        <div className="card card-pad">
          <div className="form-error">โหลดรายการคอลัมน์ไม่สำเร็จ — ลองรีเฟรชหน้าอีกครั้ง</div>
        </div>
      ) : rows.length === 0 ? (
        <Empty title="ยังไม่มีคอลัมน์ที่รองรับ" body="ระบบรองรับเลขบัญชีธนาคารของผู้ขายและพนักงานในรุ่นนี้" />
      ) : (
        <div className="table-wrap card">
          <table className="data">
            <thead>
              <tr>
                <th>คอลัมน์</th>
                <th style={{ width: 160 }}>กุญแจ</th>
                <th style={{ width: 150 }}>สถานะ</th>
                <th style={{ width: 150 }}>ตัวอย่างที่แสดง</th>
                <th style={{ width: 180 }}>หมุนล่าสุด</th>
                <th className="num" style={{ width: 130 }}>จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const done = row.key_version > 0
                const pending = encrypt.isPending && encrypt.variables?.table_name === row.table_name
                return (
                  <tr key={`${row.table_name}.${row.column_name}`}>
                    <td>
                      <div className="mono" style={{ color: 'var(--color-ivory)' }}>
                        {row.table_name}.{row.column_name}
                      </div>
                      <div className="dim" style={{ fontSize: 11, marginTop: 3 }}>
                        {number(row.encrypted_rows)} แถวเข้ารหัส · {number(row.pending_rows)} แถรรอ
                      </div>
                    </td>
                    <td>
                      {done ? (
                        <>
                          <div className="mono">v{row.key_version}</div>
                          <div className="mono dim" style={{ fontSize: 10.5, marginTop: 3 }}>{row.key_id}</div>
                        </>
                      ) : (
                        <span className="dim">ยังไม่ตั้งค่า</span>
                      )}
                    </td>
                    <td>
                      <span
                        className="chip"
                        style={{
                          color: done ? 'var(--color-emerald)' : 'var(--color-amber)',
                          borderColor: 'currentColor',
                        }}>
                        {done ? 'เข้ารหัสแล้ว' : 'รอเข้ารหัส'}
                      </span>
                    </td>
                    <td className="mono">
                      {row.sample_masked || <span className="dim">—</span>}
                    </td>
                    <td className="dim" style={{ fontSize: 11.5 }}>
                      {row.rotated_at ? thaiDateTime(row.rotated_at) : 'ยังไม่เคยหมุน'}
                    </td>
                    <td>
                      <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        {canWrite ? (
                          <button
                            className="btn btn-ghost btn-sm"
                            disabled={busy || row.pending_rows === 0}
                            onClick={() => askEncrypt(row)}>
                            {pending ? 'กำลังเข้ารหัส…' : 'เข้ารหัสคอลัมน์นี้'}
                          </button>
                        ) : (
                          <span className="dim" style={{ fontSize: 11 }}>อ่านอย่างเดียว</span>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="card card-pad" style={{ marginTop: 22 }}>
        <div className="card-label">การแสดงผลและความเป็นส่วนตัว</div>
        <p className="dim" style={{ fontSize: 11.5, lineHeight: 1.9, marginTop: 8 }}>
          หน้าจอและ API แสดงเฉพาะเลขท้ายสี่หลัก เช่น ••••1234 โดยอ่านจากส่วนที่เก็บไว้ตั้งแต่ตอนเข้ารหัส
          ไม่ได้ถอดรหัสเพื่อแสดงผล · ค่าที่เข้ารหัสแล้วจะถูกเขียนลงคอลัมน์ enc_payload และคอลัมน์ต้นฉบับถูกตั้งเป็น NULL
          ดังนั้นการคัดลอกฐานข้อมูลหรือสอบถามตรงใน SQL จะไม่เห็นเลขบัญชีเต็ม
        </p>
        <p className="dim" style={{ fontSize: 11.5, lineHeight: 1.9, marginTop: 4 }}>
          กุญแจมาจาก secretbox ของระบบที่deriveจาก JWT_SECRET · การหมุนเวอร์ชันจะเข้ารหัสใหม่ทั้งคอลัมน์
          และเก็บเวอร์ชันเดิมไว้ในซองข้อมูลของแต่ละแถว เพื่อให้แถวเก่ายังอ่านได้ระหว่างการเปลี่ยนผ่าน
        </p>
      </div>
    </>
  )
}
